import React from 'react';
import { Plus, RefreshCw, ShieldCheck, UserCheck, UserX, KeyRound, History, Save, LogOut } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const ADMIN_KEY_STORAGE = 'jpexgo_admin_key_v1';
const STAFF_SESSION_STORAGE = 'jpexgo_staff_session_v1';
const PAGE_SIZE = 10;

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const today = () => new Date().toISOString().slice(0, 10);

const ActionButton = ({ children, onClick, disabled, tone = 'default' }) => {
  const tones = {
    default: 'border-stroke bg-white/5 text-text-primary hover:bg-white/10',
    yellow: 'border-neon-yellow/30 bg-neon-yellow/10 text-neon-yellow hover:bg-neon-yellow/15',
    red: 'border-red-400/30 bg-red-400/10 text-red-200 hover:bg-red-400/15',
    green: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/15',
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-semibold transition disabled:opacity-50 ${tones[tone] || tones.default}`}
    >
      {children}
    </button>
  );
};

const Pagination = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(Number(total || 0) / pageSize));
  const from = total ? page * pageSize + 1 : 0;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div className="mt-4 flex flex-col gap-2 rounded-2xl border border-stroke bg-surface p-3 text-xs text-text-secondary sm:flex-row sm:items-center sm:justify-between">
      <div>
        Показано {from}-{to} из {total}
      </div>
      <div className="flex items-center gap-2">
        <ActionButton onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page <= 0}>Назад</ActionButton>
        <span>
          {page + 1} / {totalPages}
        </span>
        <ActionButton onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Вперёд</ActionButton>
      </div>
    </div>
  );
};

const AdminLogin = ({ value, password, onChange, onPasswordChange, onSubmit, error, loading }) => (
  <div className="min-h-screen bg-bg px-4 py-8 text-text-primary">
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center">
      <div className="w-full rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
        <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
          <ShieldCheck size={15} /> Админка
        </div>
        <h1 className="mt-3 text-2xl font-black">Вход для администратора</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Введите логин и пароль администратора. Старый ADMIN_KEY временно тоже работает.
        </p>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="Логин или ADMIN_KEY"
          className="mt-5 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        <input
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Пароль администратора"
          type="password"
          className="mt-3 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value || loading}
          className="mt-4 h-12 w-full rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60"
        >
          Войти
        </button>
      </div>
    </div>
  </div>
);

const AdminCashiersPage = () => {
  const { initData } = useTelegram();
  const initDataRaw = initData?.initData || '';
  const [adminKey, setAdminKey] = React.useState(() => {
    try {
      return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
    } catch (_) {
      return '';
    }
  });
  const [staffSessionToken, setStaffSessionToken] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STAFF_SESSION_STORAGE) || '{}')?.staffSessionToken || '';
    } catch (_) {
      return '';
    }
  });
  const [loginKey, setLoginKey] = React.useState(adminKey);
  const [loginPassword, setLoginPassword] = React.useState('');

  const [cashiers, setCashiers] = React.useState([]);
  const [history, setHistory] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [createdPin, setCreatedPin] = React.useState('');
  const [form, setForm] = React.useState({ fullName: '', pin: '' });
  const [period, setPeriod] = React.useState({ from: today(), to: today() });
  const [page, setPage] = React.useState(0);

  const hasTelegramAuth = typeof initDataRaw === 'string' && initDataRaw.includes('hash=');
  const hasAuth = Boolean(hasTelegramAuth || adminKey || staffSessionToken);
  const authBody = React.useCallback((extra = {}) => {
    if (staffSessionToken) return { staffSessionToken, ...extra };
    return hasTelegramAuth ? { initData: initDataRaw, ...extra } : { adminKey, ...extra };
  }, [adminKey, hasTelegramAuth, initDataRaw, staffSessionToken]);

  const loadCashiers = React.useCallback(async () => {
    if (!hasAuth) return;
    setLoading(true);
    setError('');
    try {
      const r = await apiPost('/api/admin/cashiers/list', authBody(period));
      setCashiers(Array.isArray(r?.cashiers) ? r.cashiers : []);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить кассиров');
      if (!hasTelegramAuth && !staffSessionToken) {
        try {
          localStorage.removeItem(ADMIN_KEY_STORAGE);
        } catch (_) {
          // ignore
        }
        setAdminKey('');
      }
      if (staffSessionToken) {
        try {
          localStorage.removeItem(STAFF_SESSION_STORAGE);
        } catch (_) {
          // ignore
        }
        setStaffSessionToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [authBody, hasAuth, hasTelegramAuth, period, staffSessionToken]);

  React.useEffect(() => {
    loadCashiers();
  }, [loadCashiers]);

  React.useEffect(() => {
    setPage(0);
  }, [period.from, period.to]);

  const login = async () => {
    const nextKey = String(loginKey || '').trim();
    if (!nextKey) return;
    setLoginLoading(true);
    setError('');
    try {
      let auth = { adminKey: nextKey };
      if (loginPassword) {
        const session = await apiPost('/api/staff/login', { login: nextKey, password: loginPassword });
        auth = { staffSessionToken: session.staffSessionToken };
        localStorage.setItem(STAFF_SESSION_STORAGE, JSON.stringify(session));
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setStaffSessionToken(session.staffSessionToken || '');
      } else {
        localStorage.setItem(ADMIN_KEY_STORAGE, nextKey);
      }
      const r = await apiPost('/api/admin/cashiers/list', { ...auth, ...period });
      setCashiers(Array.isArray(r?.cashiers) ? r.cashiers : []);
      setAdminKey(loginPassword ? '' : nextKey);
      setLoginPassword('');
    } catch (_) {
      try {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        localStorage.removeItem(STAFF_SESSION_STORAGE);
      } catch (__) {
        // ignore
      }
      setAdminKey('');
      setStaffSessionToken('');
      setError('Неверный логин, пароль или ключ администратора');
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
      localStorage.removeItem(STAFF_SESSION_STORAGE);
    } catch (_) {
      // ignore
    }
    setAdminKey('');
    setStaffSessionToken('');
    setLoginKey('');
    setLoginPassword('');
    setCashiers([]);
  };

  const createCashier = async () => {
    setActionLoading('create');
    setError('');
    setMessage('');
    setCreatedPin('');
    try {
      const r = await apiPost('/api/admin/cashiers/create', authBody(form));
      setForm({ fullName: '', pin: '' });
      setCreatedPin(r?.pin || '');
      setMessage(`Кассир создан: ${r?.cashier?.full_name || ''}`);
      await loadCashiers();
    } catch (e) {
      setError(e?.message || 'Не удалось создать кассира');
    } finally {
      setActionLoading('');
    }
  };

  const updateName = async (cashier) => {
    const fullName = window.prompt('Новое имя кассира', cashier.full_name || '');
    if (!fullName) return;
    setActionLoading(`update:${cashier.id}`);
    setError('');
    try {
      await apiPost('/api/admin/cashiers/update', authBody({ id: cashier.id, fullName }));
      await loadCashiers();
    } catch (e) {
      setError(e?.message || 'Не удалось обновить кассира');
    } finally {
      setActionLoading('');
    }
  };

  const resetPin = async (cashier) => {
    if (!window.confirm(`Сбросить PIN кассира ${cashier.full_name}? Активная сессия будет закрыта.`)) return;
    setActionLoading(`pin:${cashier.id}`);
    setError('');
    setCreatedPin('');
    try {
      const r = await apiPost('/api/admin/cashiers/reset-pin', authBody({ id: cashier.id }));
      setCreatedPin(r?.pin || '');
      setMessage(`Новый PIN для ${cashier.full_name}: ${r?.pin || ''}`);
      await loadCashiers();
    } catch (e) {
      setError(e?.message || 'Не удалось сбросить PIN');
    } finally {
      setActionLoading('');
    }
  };

  const setActive = async (cashier, isActive) => {
    const verb = isActive ? 'включить' : 'отключить';
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} кассира ${cashier.full_name}?`)) return;
    setActionLoading(`active:${cashier.id}`);
    setError('');
    try {
      await apiPost('/api/admin/cashiers/set-active', authBody({ id: cashier.id, isActive }));
      await loadCashiers();
    } catch (e) {
      setError(e?.message || 'Не удалось изменить статус кассира');
    } finally {
      setActionLoading('');
    }
  };

  const loadHistory = async (cashier) => {
    setActionLoading(`history:${cashier.id}`);
    setError('');
    try {
      const r = await apiPost('/api/admin/cashiers/history', authBody({ id: cashier.id, limit: 30 }));
      setHistory({ cashier, logs: r?.logs || [], sessions: r?.sessions || [] });
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить историю');
    } finally {
      setActionLoading('');
    }
  };

  if (!hasAuth) {
    return <AdminLogin value={loginKey} password={loginPassword} onChange={setLoginKey} onPasswordChange={setLoginPassword} onSubmit={login} error={error} loading={loginLoading} />;
  }

  const totalPickups = cashiers.reduce((sum, cashier) => sum + Number(cashier.pickups_count || 0), 0);
  const totalPages = Math.max(1, Math.ceil(cashiers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagedCashiers = cashiers.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="min-h-screen bg-bg px-4 py-5 text-text-primary">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
                <ShieldCheck size={15} /> Админка
              </div>
              <h1 className="mt-3 text-2xl font-black tracking-tight">Кассиры</h1>
              <p className="mt-1 max-w-2xl text-sm text-text-secondary">
                Добавление сотрудников пункта выдачи, сброс PIN, отключение доступа и просмотр действий.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ActionButton onClick={loadCashiers} disabled={loading} tone="yellow">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Обновить
              </ActionButton>
              {!hasTelegramAuth ? (
                <ActionButton onClick={logout} disabled={loading}>
                  <LogOut size={15} /> Выйти
                </ActionButton>
              ) : null}
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-stroke bg-white/5 p-3">
              <div className="text-[11px] uppercase tracking-wide text-text-muted">Всего кассиров</div>
              <div className="mt-1 text-2xl font-black">{cashiers.length}</div>
            </div>
            <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-3">
              <div className="text-[11px] uppercase tracking-wide text-emerald-100/75">Активные</div>
              <div className="mt-1 text-2xl font-black text-emerald-100">{cashiers.filter((c) => c.is_active).length}</div>
            </div>
            <div className="rounded-2xl border border-neon-yellow/20 bg-neon-yellow/10 p-3">
              <div className="text-[11px] uppercase tracking-wide text-text-muted">Выдач за период</div>
              <div className="mt-1 text-2xl font-black">{totalPickups}</div>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <label className="block">
              <span className="text-xs text-text-secondary">С даты</span>
              <input type="date" value={period.from} onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <label className="block">
              <span className="text-xs text-text-secondary">По дату</span>
              <input type="date" value={period.to} onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))} className="mt-1 h-11 w-full rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
            </label>
            <div className="flex items-end">
              <ActionButton onClick={loadCashiers} disabled={loading}>Применить</ActionButton>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-stroke bg-black/10 p-4">
            <div className="text-sm font-semibold">Добавить кассира</div>
            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_180px_auto]">
              <input value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} placeholder="ФИО кассира" className="h-11 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
              <input value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 3) }))} placeholder="PIN или пусто" inputMode="numeric" className="h-11 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
              <button type="button" onClick={createCashier} disabled={actionLoading === 'create' || form.fullName.trim().length < 2} className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60">
                <Plus size={16} /> Добавить
              </button>
            </div>
            <div className="mt-2 text-xs text-text-secondary">
              Если PIN оставить пустым, система сгенерирует 3 цифры автоматически. PIN показывается только один раз.
            </div>
          </div>

          {createdPin ? (
            <div className="mt-4 rounded-2xl border border-neon-yellow/30 bg-neon-yellow/10 p-4 text-sm text-neon-yellow">
              Новый PIN: <span className="font-mono text-xl font-black">{createdPin}</span>. Передайте его кассиру и не отправляйте в общий чат.
            </div>
          ) : null}
          {message ? <div className="mt-4 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-3 text-sm text-emerald-100">{message}</div> : null}
          {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        </div>

        <div className="mt-4 grid gap-3">
          {pagedCashiers.map((cashier) => (
            <div key={cashier.id} className="rounded-3xl border border-stroke bg-surface p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="truncate text-lg font-black">{cashier.full_name}</div>
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cashier.is_active ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' : 'border-red-400/30 bg-red-400/10 text-red-200'}`}>
                      {cashier.is_active ? 'Активен' : 'Отключён'}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 text-xs text-text-secondary md:grid-cols-3">
                    <div>Создан: {formatDate(cashier.created_at)}</div>
                    <div>Последний вход: {formatDate(cashier.last_login_at)}</div>
                    <div>Выдач: <span className="text-text-primary">{cashier.pickups_count || 0}</span></div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <ActionButton onClick={() => updateName(cashier)} disabled={!!actionLoading}><Save size={15} /> Имя</ActionButton>
                  <ActionButton onClick={() => resetPin(cashier)} disabled={!!actionLoading} tone="yellow"><KeyRound size={15} /> Сброс PIN</ActionButton>
                  <ActionButton onClick={() => setActive(cashier, !cashier.is_active)} disabled={!!actionLoading} tone={cashier.is_active ? 'red' : 'green'}>
                    {cashier.is_active ? <UserX size={15} /> : <UserCheck size={15} />}
                    {cashier.is_active ? 'Отключить' : 'Включить'}
                  </ActionButton>
                  <ActionButton onClick={() => loadHistory(cashier)} disabled={!!actionLoading}><History size={15} /> История</ActionButton>
                </div>
              </div>
            </div>
          ))}
          {!loading && !cashiers.length ? (
            <div className="rounded-3xl border border-stroke bg-surface p-6 text-center text-sm text-text-secondary">
              Кассиров пока нет. Добавьте первого сотрудника выше.
            </div>
          ) : null}
          {cashiers.length > PAGE_SIZE ? (
            <Pagination page={safePage} total={cashiers.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
          ) : null}
        </div>

        {history ? (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-4 md:items-center">
            <div className="max-h-[86vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-stroke bg-[#111827] p-5 text-text-primary shadow-2xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xl font-black">История кассира</div>
                  <div className="mt-1 text-sm text-text-secondary">{history.cashier.full_name}</div>
                </div>
                <button type="button" onClick={() => setHistory(null)} className="rounded-xl border border-stroke px-3 py-2 text-sm">Закрыть</button>
              </div>

              <div className="mt-5">
                <div className="text-sm font-semibold">Последние сессии</div>
                <div className="mt-2 space-y-2">
                  {history.sessions.map((session) => (
                    <div key={session.id} className="rounded-2xl border border-stroke bg-white/5 p-3 text-xs text-text-secondary">
                      Вход: <span className="text-text-primary">{formatDate(session.started_at)}</span>
                      <span className="mx-2">/</span>
                      Выход: <span className="text-text-primary">{formatDate(session.ended_at)}</span>
                    </div>
                  ))}
                  {!history.sessions.length ? <div className="text-xs text-text-secondary">Сессий пока нет.</div> : null}
                </div>
              </div>

              <div className="mt-5">
                <div className="text-sm font-semibold">Действия</div>
                <div className="mt-2 space-y-2">
                  {history.logs.map((log) => (
                    <div key={log.id} className="rounded-2xl border border-stroke bg-white/5 p-3 text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-text-primary">{log.action}</span>
                        <span className="text-text-secondary">{formatDate(log.created_at)}</span>
                      </div>
                      <div className="mt-1 text-text-secondary">{log.entity_type || '-'} #{log.entity_id || '-'}</div>
                    </div>
                  ))}
                  {!history.logs.length ? <div className="text-xs text-text-secondary">Действий пока нет.</div> : null}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminCashiersPage;
